import { useCallback, useEffect, useRef, useState } from 'react'

import MessagesService from '@/services/messages.service'
import { notificationService } from '@/services/notification.service'
import { useAuthStore } from '@/stores/authStore'
import { useInstancesStore } from '@/stores/instancesStore'
import { getStoredActiveChat, useMessagesStore } from '@/stores/messagesStore'
import { useWebSocketStore } from '@/stores/websocketStore'
import type {
  ConversationUpdatedEvent,
  InstanceStatusEvent,
  MessageReceivedEvent,
  MessageStatusUpdatedEvent,
  TypingEvent,
  WebSocketHookReturn,
  WsConnectionState,
} from '@/types/websocket'
import { WS_URL } from '@/utils/app-constants'

const DEFAULT_BASE_MS = 1_000
const DEFAULT_MAX_MS = 30_000
const DEFAULT_MAX_ATTEMPTS = 10
const MESSAGE_DEDUP_TTL_MS = 5_000
const NON_RETRYABLE_CLOSE_CODES = new Set([1008, 4001, 4003])

function parseJwtPayload(token: string): { exp?: number } | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
    return JSON.parse(window.atob(padded)) as { exp?: number }
  } catch {
    return null
  }
}

function isTokenValid(token: string): boolean {
  const payload = parseJwtPayload(token)
  if (!payload?.exp) return false
  return payload.exp * 1000 > Date.now() + 30_000
}

function calcReconnectDelay(
  attempt: number,
  baseMs = DEFAULT_BASE_MS,
  maxMs = DEFAULT_MAX_MS,
): number {
  const exponential = Math.min(baseMs * 2 ** attempt, maxMs)
  const jitter = Math.random() * 0.3 * exponential
  return Math.floor(exponential + jitter)
}

function buildNotificationBody(payload: MessageReceivedEvent): string {
  const content = payload.message.content?.trim()
  if (content) return content

  switch (payload.message.message_type) {
    case 'audio':
      return 'Nova mensagem de audio'
    case 'image':
      return 'Nova imagem recebida'
    case 'video':
      return 'Novo video recebido'
    case 'document':
      return 'Novo documento recebido'
    default:
      return 'Nova mensagem recebida'
  }
}

export function useWebSocket(instanceId?: string): WebSocketHookReturn {
  const [connectionState, setConnectionState] = useState<WsConnectionState>('DISCONNECTED')
  const [reconnectAttempt, setReconnectAttempt] = useState(0)

  const socketRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const connectRef = useRef<() => Promise<void>>(async () => undefined)
  const attemptRef = useRef(0)
  const circuitOpenRef = useRef(false)
  const unmountedRef = useRef(false)
  const recentMessageIdsRef = useRef(new Map<string, number>())

  const token = useAuthStore((state: { token: any }) => state.token)
  const addMessage = useMessagesStore((state: { addMessage: any }) => state.addMessage)
  const updateMessageStatus = useMessagesStore((state: { updateMessageStatus: any }) => state.updateMessageStatus)
  const setTyping = useMessagesStore((state: { setTyping: any }) => state.setTyping)
  const upsertConversation = useMessagesStore((state: { upsertConversation: any }) => state.upsertConversation)
  const updateInstance = useInstancesStore((state: { updateInstance: any }) => state.updateInstance)
  const setWsStatus = useWebSocketStore((state: { setStatus: any }) => state.setStatus)
  const resetWsStatus = useWebSocketStore((state: { reset: any }) => state.reset)

  const updateStatus = useCallback(
    (state: WsConnectionState, attempt = attemptRef.current) => {
      setConnectionState(state)
      setReconnectAttempt(attempt)
      setWsStatus(state, attempt)
    },
    [setWsStatus],
  )

  const cleanup = useCallback(() => {
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current)
      pingTimerRef.current = null
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
    if (socketRef.current) {
      socketRef.current.onopen = null
      socketRef.current.onmessage = null
      socketRef.current.onerror = null
      socketRef.current.onclose = null
      if (
        socketRef.current.readyState === WebSocket.OPEN ||
        socketRef.current.readyState === WebSocket.CONNECTING
      ) {
        socketRef.current.close(1000, 'Cleanup')
      }
      socketRef.current = null
    }
  }, [])

  const pruneRecentMessageIds = useCallback(() => {
    const now = Date.now()
    for (const [messageId, expiresAt] of recentMessageIdsRef.current.entries()) {
      if (expiresAt <= now) {
        recentMessageIdsRef.current.delete(messageId)
      }
    }
  }, [])

  const ensureValidToken = useCallback(async (): Promise<string | null> => {
    const currentToken = useAuthStore.getState().token ?? token
    if (!currentToken) {
      return null
    }

    if (isTokenValid(currentToken)) {
      return currentToken
    }

    try {
      await useAuthStore.getState().refreshAccessToken()
    } catch {
      return null
    }

    const refreshedToken = useAuthStore.getState().token
    if (refreshedToken && isTokenValid(refreshedToken)) {
      return refreshedToken
    }

    return null
  }, [token])

  const scheduleReconnect = useCallback(() => {
    if (unmountedRef.current || circuitOpenRef.current) return

    const attempt = attemptRef.current
    if (attempt >= DEFAULT_MAX_ATTEMPTS) {
      circuitOpenRef.current = true
      console.warn(`[WS] Circuit breaker aberto apos ${DEFAULT_MAX_ATTEMPTS} falhas consecutivas.`)
      updateStatus('CIRCUIT_OPEN', attempt)
      return
    }

    const delay = calcReconnectDelay(attempt)
    console.info(`[WS] Reconexao agendada: tentativa ${attempt + 1}/${DEFAULT_MAX_ATTEMPTS} em ${delay}ms`)
    updateStatus('RECONNECTING', attempt + 1)

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
    }
    reconnectTimerRef.current = setTimeout(() => {
      attemptRef.current += 1
      setReconnectAttempt(attemptRef.current)
      void connectRef.current()
    }, delay)
  }, [updateStatus])

  const reconcileActiveConversation = useCallback(
    (_wsInstanceId: string, payload: MessageReceivedEvent) => {
      const store = useMessagesStore.getState()
      const activeConversation = store.activeConversation
      if (!activeConversation) return

      const sameConversation =
        payload.conversation_id === activeConversation.id ||
        payload.message.contact_id === activeConversation.contact_id

      if (!sameConversation) return

      store.markConversationRead(activeConversation.contact_id, payload.conversation_id)
    },
    [],
  )

  const maybeNotifyIncomingMessage = useCallback(
    (payload: MessageReceivedEvent) => {
      if (!useAuthStore.getState().user?.native_notifications_enabled) return

      const store = useMessagesStore.getState()
      const activeConversation = store.activeConversation
      const isFocused = typeof document !== 'undefined' && document.visibilityState === 'visible' && document.hasFocus()
      const isActiveConversation =
        activeConversation &&
        (
          payload.conversation_id === activeConversation.id ||
          payload.message.contact_id === activeConversation.contact_id
        )

      if (isFocused && isActiveConversation) {
        return
      }

      if (payload.message.direction !== 'inbound') {
        return
      }

      const contactName =
        payload.contact?.push_name ||
        payload.contact?.name ||
        payload.conversation?.contact?.push_name ||
        payload.conversation?.contact?.name ||
        payload.message.from_jid

      notificationService.notify({
        dedupeKey: payload.message.message_id_wa ?? payload.message.id,
        title: contactName,
        body: buildNotificationBody(payload),
        tag: `conversation:${payload.conversation_id}`,
        fallbackMessage: `Nova mensagem de ${contactName}`,
        onClick: () => {
          if (window.location.pathname !== '/chat') {
            window.location.href = '/chat'
          }
        },
      })
    },
    [],
  )

  const maybeNotifyInstanceStatus = useCallback((payload: InstanceStatusEvent) => {
    if (!useAuthStore.getState().user?.native_notifications_enabled) return
    if (payload.status !== 'connected' && payload.status !== 'disconnected') return

    notificationService.notify({
      dedupeKey: `instance-status:${payload.instance_id}:${payload.status}`,
      title: payload.status === 'connected' ? 'Instância conectada' : 'Instância desconectada',
      body: `A instância ${payload.instance_id} está ${payload.status === 'connected' ? 'conectada' : 'desconectada'}.`,
      tag: `instance:${payload.instance_id}`,
      fallbackMessage: payload.status === 'connected' ? 'Instância conectada.' : 'Instância desconectada.',
    })
  }, [])

  const handleEvent = useCallback(
    (event: MessageEvent<string>) => {
      let payload: { event: string; data: unknown }
      try {
        payload = JSON.parse(event.data) as { event: string; data: unknown }
      } catch {
        return
      }

      console.debug('[WS_EVENT]', payload.event, payload.data)

      if (payload.event === 'new_message') {
        const data = payload.data as MessageReceivedEvent
        pruneRecentMessageIds()

        const messageId = (data.message.provider_message_id ?? data.message.message_id_wa ?? '').trim()
        if (messageId) {
          if (recentMessageIdsRef.current.has(messageId)) {
            return
          }
          recentMessageIdsRef.current.set(messageId, Date.now() + MESSAGE_DEDUP_TTL_MS)
        }

        addMessage(data.message)
        maybeNotifyIncomingMessage(data)
        if (data.conversation) {
          upsertConversation(data.conversation)
        }

        const store = useMessagesStore.getState()
        const knownConversation = store.conversations.some(
          (conversation: { id: string; contact_id: string | undefined }) =>
            conversation.id === data.conversation_id || conversation.contact_id === data.message.contact_id,
        )

        if (instanceId) {
          reconcileActiveConversation(instanceId, data)

          if (!knownConversation && data.message.contact_id) {
            if (instanceId === '__all__') {
              store.fetchWorkspaceConversations().catch(() => undefined)
            } else {
              store.fetchConversations(instanceId).catch(() => undefined)
            }
          }
        }
        return
      }

      if (payload.event === 'message_status_update' || payload.event === 'message_ack') {
        const data = payload.data as MessageStatusUpdatedEvent
        updateMessageStatus(data.message_id, data.status)
        return
      }

      if (payload.event === 'conversation_update') {
        const data = payload.data as ConversationUpdatedEvent
        upsertConversation(data.conversation)
        return
      }

      if (payload.event === 'instance_status') {
        const data = payload.data as InstanceStatusEvent
        updateInstance(data.instance_id, {
          status: data.status,
          qr_code_data: data.qr_code_data,
          phone_number: data.phone_number ?? undefined,
          profile_picture_url: data.profile_picture_url ?? undefined,
        })
        maybeNotifyInstanceStatus(data)
        return
      }

      if (payload.event === 'typing_start' || payload.event === 'typing_stop') {
        const data = payload.data as TypingEvent
        setTyping(data.jid, payload.event === 'typing_start' || Boolean(data.is_typing))
      }
    },
    [
      addMessage,
      instanceId,
      maybeNotifyIncomingMessage,
      maybeNotifyInstanceStatus,
      pruneRecentMessageIds,
      reconcileActiveConversation,
      setTyping,
      updateInstance,
      updateMessageStatus,
      upsertConversation,
    ],
  )

  const connect = useCallback(async () => {
    if (unmountedRef.current) return

    const validToken = await ensureValidToken()
    if (!validToken) {
      console.warn('[WS] Conexao abortada: token ausente ou expirado.')
      cleanup()
      attemptRef.current = 0
      circuitOpenRef.current = false
      updateStatus('DISCONNECTED', 0)
      return
    }

    cleanup()

    const nextState = attemptRef.current > 0 ? 'RECONNECTING' : 'CONNECTING'
    updateStatus(nextState, attemptRef.current)

    const url = new URL('/ws', WS_URL)
    url.searchParams.set('token', validToken)
    if (instanceId) {
      url.searchParams.set('instance_id', instanceId)
      if (instanceId !== '__all__') {
        const storedActiveChat = getStoredActiveChat()
        if (storedActiveChat?.instance_id === instanceId) {
          if (storedActiveChat.last_message_id) {
            url.searchParams.set('last_message_id', storedActiveChat.last_message_id)
          }
          url.searchParams.set('conversation_id', storedActiveChat.conversation_id)
          url.searchParams.set('contact_id', storedActiveChat.contact_id)
        }
      }
    }

    console.info(`[WS] Conectando: ${url.origin}/ws (tentativa ${attemptRef.current})`)

    const socket = new WebSocket(url.toString())
    socketRef.current = socket

    socket.onopen = () => {
      if (unmountedRef.current) {
        socket.close(1000, 'Unmounted')
        return
      }

      attemptRef.current = 0
      circuitOpenRef.current = false
      updateStatus('CONNECTED', 0)
      pingTimerRef.current = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ event: 'ping' }))
        }
      }, 25_000)
      console.info('[WS] Conexao estabelecida.')
    }

    socket.onmessage = handleEvent

    socket.onerror = () => {
      console.error('[WS] Erro na conexao WebSocket.')
    }

    socket.onclose = (event) => {
      if (unmountedRef.current) return

      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current)
        pingTimerRef.current = null
      }

      console.warn(
        `[WS] Conexao encerrada: codigo=${event.code} motivo="${event.reason}" limpa=${event.wasClean}`,
      )

      if (NON_RETRYABLE_CLOSE_CODES.has(event.code)) {
        updateStatus('DISCONNECTED', attemptRef.current)
        return
      }

      scheduleReconnect()
    }
  }, [cleanup, ensureValidToken, handleEvent, instanceId, scheduleReconnect, updateStatus])

  connectRef.current = connect

  const reconnect = useCallback(() => {
    circuitOpenRef.current = false
    attemptRef.current = 0
    updateStatus('CONNECTING', 0)
    void connectRef.current()
  }, [updateStatus])

  useEffect(() => {
    unmountedRef.current = false

    if (token) {
      attemptRef.current = 0
      circuitOpenRef.current = false
      void connect()
    } else {
      cleanup()
      updateStatus('DISCONNECTED', 0)
    }

    return () => {
      unmountedRef.current = true
      cleanup()
      resetWsStatus()
    }
  }, [cleanup, connect, resetWsStatus, token])

  const sendMessage = useCallback(
    async (toJid: string, content: string, instId: string) => {
      const message = await MessagesService.sendMessage(instId, {
        to_jid: toJid,
        content,
      })
      addMessage(message)
    },
    [addMessage],
  )

  const setTypingEmit = useCallback(
    (_instId: string, jid: string, isTyping: boolean) => {
      setTyping(jid, isTyping)
    },
    [setTyping],
  )

  const markAsRead = useCallback(
    async (instId: string, messageId: string) => {
      await MessagesService.markAsRead(instId, messageId)
      updateMessageStatus(messageId, 'read')
    },
    [updateMessageStatus],
  )

  return {
    connectionState,
    connected: connectionState === 'CONNECTED',
    sendMessage,
    setTyping: setTypingEmit,
    markAsRead,
    reconnect,
    reconnectAttempt,
  }
}
